import { TableColumn } from './types'

/** Row action columns (copy/delete, etc.) must stay visible and out of the column menu. */
const ALWAYS_VISIBLE_COLUMN_KEYS = new Set(['actions'])

export function isColumnMenuToggleable<T>(column: TableColumn<T>): boolean {
  return Boolean(column.key) && !column.required && !ALWAYS_VISIBLE_COLUMN_KEYS.has(column.key)
}

export function withForcedVisibleColumns<T>(keys: Set<string>, columns: TableColumn<T>[]): Set<string> {
  const next = new Set(keys)
  for (const col of columns) {
    if (!col?.key) continue
    if (col.required || ALWAYS_VISIBLE_COLUMN_KEYS.has(col.key)) {
      next.add(col.key)
    }
  }
  return next
}

// Ensure at least one column is required
export function ensureRequiredColumn<T>(columns: TableColumn<T>[] | undefined): TableColumn<T>[] {
  // Defensive check - ensure columns is always an array
  if (columns == null || !Array.isArray(columns) || columns.length === 0) {
    return []
  }
  
  // Filter out any null/undefined columns first
  const validColumns = columns.filter(col => col != null) as TableColumn<T>[]
  if (validColumns.length === 0) {
    return []
  }
  
  const hasRequired = validColumns.some(col => col.required)
  if (hasRequired) {
    return validColumns
  }
  
  // Find 'id' column first, then 'name', then first column
  const idColumnIndex = validColumns.findIndex(col => col.key && col.key.toLowerCase() === 'id')
  const nameColumnIndex = validColumns.findIndex(col => col.key && col.key.toLowerCase() === 'name')
  
  const indexToRequire = idColumnIndex >= 0 ? idColumnIndex : (nameColumnIndex >= 0 ? nameColumnIndex : 0)
  
  return validColumns.map((col, index) => ({
    ...col,
    required: index === indexToRequire
  }))
}

// Toggle column visibility
export function toggleColumnVisibility<T>(
  columnKey: string,
  columns: TableColumn<T>[],
  visibleColumns: Set<string>,
  setVisibleColumns: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  const column = columns.find(col => col.key === columnKey)
  if (!column || !isColumnMenuToggleable(column)) return

  setVisibleColumns(prev => {
    const next = new Set(prev)
    if (next.has(columnKey)) {
      // Check if this is the last visible column
      const visibleCount = Array.from(next).length
      if (visibleCount <= 1) {
        // Don't allow hiding the last visible column
        return prev
      }
      next.delete(columnKey)
    } else {
      next.add(columnKey)
    }
    return next
  })
}

// Handle row selection
export function handleSelectRow(
  index: number,
  checked: boolean,
  selectedRows: Set<number>,
  onRowSelect?: (selectedIndices: Set<number>) => void
) {
  if (!onRowSelect) return

  const newSelected = new Set(selectedRows)
  if (checked) {
    newSelected.add(index)
  } else {
    newSelected.delete(index)
  }
  onRowSelect(newSelected)
}

// Handle select all
export function handleSelectAll<T>(
  checked: boolean,
  displayedData: T[],
  onRowSelect?: (selectedIndices: Set<number>) => void
) {
  if (!onRowSelect) return

  if (checked) {
    onRowSelect(new Set(displayedData.map((_, index) => index)))
  } else {
    onRowSelect(new Set())
  }
}

// Get selected data for toolbar
export function getSelectedData<T>(
  selectedRows: Set<number>,
  data: T[]
): T[] {
  return Array.from(selectedRows).map(index => data[index]).filter(Boolean)
}
